import React from 'react';
import Reveal from './Reveal';
import ScanRing from './ScanRing';

/**
 * Compact hero used on inner pages (How it works, Features, About, Contact,
 * legal, etc.). Light or dark tone, eyebrow + title + lead, optional right
 * slot. Inner content fades up via <Reveal>; dark tone gets a soft breathing
 * teal halo (the brand-correct version of the old blur blob).
 */
export default function PageHero({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  right,
  tone = 'light' // 'light' | 'dark'
}) {
  const isDark = tone === 'dark';
  return (
    <section
      className={
        isDark
          ? 'relative isolate overflow-hidden bg-ink-900 text-white'
          : 'relative isolate overflow-hidden bg-white text-ink-900 border-b border-ink-200'
      }
    >
      {/* Brand-correct gradient flair using Vitality Teal + Intelligence Navy */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background: isDark
            ? `radial-gradient(50% 50% at 80% 0%, rgba(14,178,171,0.30), transparent 60%),
               radial-gradient(40% 40% at 0% 100%, rgba(2,62,115,0.45), transparent 60%)`
            : `radial-gradient(50% 50% at 100% 0%, rgba(192,239,236,0.55), transparent 60%),
               radial-gradient(40% 40% at 0% 100%, rgba(230,238,245,0.50), transparent 60%)`
        }}
      />

      {/* Soft breathing scan ring — only on dark heroes so light pages stay calm. */}
      {isDark && (
        <ScanRing className="absolute -top-1/3 right-[-12%] w-[40rem] h-[40rem] -z-10" tone="teal" />
      )}

      <div className="container-x py-20 sm:py-24">
        <div
          className={`grid gap-10 ${right ? 'lg:grid-cols-12' : 'lg:grid-cols-1'} ${
            align === 'center' ? 'text-center' : 'text-left'
          }`}
        >
          <Reveal className={right ? 'lg:col-span-7' : 'max-w-3xl'}>
            {eyebrow && (
              <span
                className={
                  isDark
                    ? 'inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase bg-white/10 text-brand-200 border border-white/10 backdrop-blur'
                    : 'eyebrow'
                }
              >
                {eyebrow}
              </span>
            )}
            <h1
              className={`mt-5 tracking-tight font-bold ${
                isDark ? 'text-white' : 'text-ink-900'
              } text-4xl sm:text-5xl md:text-6xl leading-[1.05]`}
            >
              {title}
            </h1>
            {subtitle && (
              <p
                className={`mt-5 text-lg sm:text-xl leading-relaxed max-w-2xl ${
                  isDark ? 'text-ink-300' : 'text-ink-600'
                } ${align === 'center' ? 'mx-auto' : ''}`}
              >
                {subtitle}
              </p>
            )}
          </Reveal>

          {right && (
            <Reveal className="lg:col-span-5" delay={120}>
              {right}
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
